/**
 * groupBasketLink: the two addresses of an "Order together" basket and the
 * link token a new account carries.
 *
 *   /g/<token>              the link the host shares (contract section 5.5).
 *                           The join page lives here, signed in or not.
 *   /group-basket/<id>      the basket itself, for its members.
 *
 * A visitor who opens a link and then creates an account sends the token as
 * `groupBasketToken` on the account creation call (contract section 8), so the
 * server can record where the account came from. The token is kept in this
 * browser until then, like the referral code (utils/referralCode.js), and the
 * server decides everything else.
 */

const TOKEN_STORAGE_KEY = "lusuku.groupBasketToken";

/* Baskets end and their links stop working 7 days later (contract 5.5). */
const TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/* The token is 22 URL-safe characters; accept a little either side. */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

export const isGroupBasketToken = (token) =>
  typeof token === "string" && TOKEN_PATTERN.test(token);

export const groupJoinPath = (token) => `/g/${encodeURIComponent(token)}`;

export const groupBasketPath = (basketId) => `/group-basket/${encodeURIComponent(basketId)}`;

const readStoredToken = () => {
  try {
    return JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY) || "null");
  } catch (_) {
    return null;
  }
};

const writeStoredToken = (value) => {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(value));
  } catch (_) {
    /* storage disabled: the token is simply not carried */
  }
};

/* Kept when a signed-out visitor opens a link. A newer link replaces it. */
export const rememberGroupBasketToken = (token, now = Date.now()) => {
  if (!isGroupBasketToken(token)) return;
  const stored = readStoredToken();
  if (stored?.token === token) return;
  writeStoredToken({ token, savedAt: now, accountCreated: false });
};

const liveStoredToken = (now = Date.now()) => {
  const stored = readStoredToken();
  if (!stored?.token || !(now - Number(stored.savedAt) < TOKEN_TTL_MS)) return null;
  return stored;
};

/* The token an account creation should still carry, or null. */
export const pendingGroupBasketToken = (now = Date.now()) => {
  const stored = liveStoredToken(now);
  return stored && !stored.accountCreated ? stored.token : null;
};

/*
 * The server has just created an account (signInOutcome). It recorded the
 * token then, once, so the token is not sent again; the note stays so the
 * join that follows can say the joiner is new.
 */
export const noteGroupLinkAccountCreated = () => {
  const stored = liveStoredToken();
  if (stored && !stored.accountCreated) writeStoredToken({ ...stored, accountCreated: true });
};

/* Did this browser create its account from this link? For group_basket_join. */
export const accountCreatedFromGroupLink = (token) => {
  const stored = liveStoredToken();
  return Boolean(stored && stored.token === token && stored.accountCreated);
};

export const clearGroupBasketToken = () => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (_) {
    /* nothing to clear */
  }
};

/* The field account-creation requests carry; absent when there is no link. */
export const groupBasketTokenField = () => {
  const token = pendingGroupBasketToken();
  return token ? { groupBasketToken: token } : {};
};
